/**
 * 预习卡（孩子端）—— `/student/dictation/:sessionId/preview`。
 *
 * ## 用户原话（这一页存在的理由）
 *
 * > "家长端布置完听写任务并点击听写，右上角有一个「先预习」的功能。
 * >  我觉得孩子那边也要加一个这个入口，在听写之前，他可以先预习，点进去预习。"
 *
 * 所以孩子点「开始听写」进到播放器之后，**右上角**（与家长端同一个位置）有一个
 * 「先预习一下」，点进去就是这一页：这一批要听写的词按田字格 + 拼音列出来，
 * 点某个字能看笔顺动画。看完点「回去开始听写」，回到**同一批**的播放器再按
 * 「开始播放」。
 *
 * ## 为什么预习放在"开始听写之后"而不是之前
 *
 * 批量任务是**孩子点「开始听写」时后端才取词**（`next-batch`）—— 在那之前
 * "这一批是哪 10 个词"根本不存在，没有东西可预习。
 * 而播放器**不会自动出声**（浏览器要求用户手势，见 `ParentDictationSessionPage`
 * 文件头），所以"进来 → 先预习 → 回去按开始"这条路上，孩子一个词都还没听到，
 * 预习的时机是对的。
 *
 * ## 与家长端的关系
 *
 * 同一个面板（`DictationPreviewPanel`），差别只有：外壳是孩子端、
 * 链接是 `/student/...`、**没有**"看这次的全部题目"（那是家长路由，孩子点进去 403）。
 */
import { useParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { ErrorState } from '../../components/ui/Bits';
import { Skeleton } from '../../components/ui/Card';
import { DictationPreviewPanel } from '../../components/dictation/DictationPreviewPanel';
import { toErrorMessage } from '../../lib/api/error';
import { useMyStudent } from '../../lib/useMyStudent';

export function StudentDictationPreviewPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const my = useMyStudent();

  if (my.isLoading) {
    return (
      <AppShell kind="student" title="先预习一下">
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (!my.studentId) {
    return (
      <AppShell kind="student" title="先预习一下">
        <ErrorState
          message={toErrorMessage(my.error) || '没能确认你的学生档案，请稍后重试'}
          onRetry={my.refetch}
        />
      </AppShell>
    );
  }

  return (
    <DictationPreviewPanel
      kind="student"
      studentId={my.studentId}
      sessionId={sessionId}
      playerHref={`/student/dictation/${sessionId}`}
      listHref="/student/dictation"
      playerHint="回上一页按「开始播放」"
    />
  );
}
